import React, { useEffect } from "react";
import { GuideLayout } from "./GuideLayout";
import { trackEvent } from "../../analytics";

const sections = [
  {
    id: "what-is-esrs",
    heading: "What are ESRS standards?",
    body: [
      "European Sustainability Reporting Standards, or ESRS, define how companies disclose sustainability information under CSRD. They help make ESG reporting more structured, comparable, and useful for investors, regulators, customers, and leadership teams.",
      "For companies, ESRS compliance means moving beyond general sustainability narratives toward evidence-based disclosures supported by policies, metrics, targets, governance processes, and materiality analysis."
    ]
  },
  {
    id: "why-it-matters",
    heading: "Why ESRS compliance matters",
    body: [
      "ESRS compliance improves the quality and consistency of sustainability reporting. It also helps organizations understand ESG risks, demonstrate governance maturity, respond to enterprise customer requests, and prepare credible investor communications.",
      "Companies that wait until reporting deadlines often discover data gaps too late. A structured ESRS workflow helps teams identify weak areas early and assign responsibility across finance, sustainability, HR, operations, procurement, and leadership."
    ]
  },
  {
    id: "core-areas",
    heading: "Core ESRS reporting areas",
    body: [
      "ESRS disclosures cover cross-cutting requirements and topic-specific standards across environmental, social, and governance themes.",
      "The most common preparation areas include governance, strategy, impacts, risks and opportunities, metrics, targets, climate-related disclosures, workforce policies, value chain information, and business conduct."
    ],
    bullets: [
      "Governance and oversight of sustainability matters",
      "Strategy and business model resilience",
      "Double materiality assessment process",
      "Climate, workforce, social, and governance disclosures",
      "Metrics, targets, evidence, and progress tracking"
    ]
  },
  {
    id: "ai-workflow",
    heading: "How AI supports ESRS reporting workflows",
    body: [
      "AI can help teams interpret ESG inputs, structure evidence, surface missing information, and produce reviewable first drafts of disclosures. It is most useful when connected to a controlled workflow with human approval, version control, and clear accountability.",
      "Ecovanta helps organizations convert fragmented sustainability inputs into structured ESRS-aligned reporting outputs, gap insights, and board-ready summaries."
    ]
  },
  {
    id: "checklist",
    heading: "ESRS readiness checklist",
    body: [
      "A practical ESRS readiness program should start with scoping, data ownership, materiality, and gap remediation before final disclosure drafting."
    ],
    bullets: [
      "Confirm applicable ESRS topics and disclosure requirements",
      "Assign internal owners for each data category",
      "Run a double materiality assessment",
      "Map available evidence and missing metrics",
      "Prioritize remediation actions by risk and reporting deadline",
      "Draft disclosures and validate them internally"
    ]
  }
];

const faqs = [
  {
    question: "What does ESRS stand for?",
    answer:
      "ESRS stands for European Sustainability Reporting Standards. These standards define the structure and content of sustainability disclosures under CSRD."
  },
  {
    question: "Is ESRS only relevant to large companies?",
    answer:
      "Large companies are a primary focus, but SMEs and suppliers may also face indirect pressure when customers, lenders, or investors request ESRS-aligned ESG information."
  },
  {
    question: "How does ESRS relate to CSRD?",
    answer:
      "CSRD is the directive that expands sustainability reporting requirements, while ESRS provides the detailed reporting standards companies use to prepare disclosures."
  },
  {
    question: "Can software help with ESRS compliance?",
    answer:
      "Yes. Software can help structure ESG data collection, map evidence, identify gaps, draft disclosures, and prepare review-ready reporting outputs."
  }
];

export default function ESRSComplianceGuide() {
  useEffect(() => {
    trackEvent("guide_view", {
      guide: "esrs_compliance",
      path: window.location.pathname
    });
  }, []);

  return (
    <GuideLayout
      eyebrow="ESRS Compliance Guide"
      title="ESRS Compliance: How Companies Can Prepare Structured Sustainability Disclosures"
      description="Learn what ESRS compliance means, why it matters under CSRD, and how AI-assisted ESG reporting can help teams prepare structured, evidence-based sustainability disclosures."
      sections={sections}
      faqs={faqs}
      ctaTitle="Build ESRS-ready sustainability reporting with Ecovanta"
      ctaText="Ecovanta helps teams organize ESG data, identify disclosure gaps, draft reviewable ESRS-style narratives, and prepare leadership-ready sustainability reporting packs."
      onCtaClick={() =>
        trackEvent("demo_click", {
          source: "esrs_compliance_guide",
          path: window.location.pathname
        })
      }
    />
  );
}